import React, { useState, useEffect } from "react";
import {
  MessageSquare,
  ThumbsUp,
  Paperclip,
  User,
  Calendar,
  Filter,
  Search,
  ChevronDown,
  ChevronUp,
  Eye,
  Trash2,
  ExternalLink,
  FileText,
  Mail,
  Link as LinkIcon,
  Building2,
  Briefcase,
  Users,
  Phone,
  RefreshCw,
  TrendingUp,
  AlertCircle,
  Send,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import apiService from "../../services/api";
import { useTheme } from "../../contexts/ThemeContext";

const FeedItems = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [feedItems, setFeedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    parentType: "all",
    type: "all",
    dateRange: "all",
    search: "",
  });
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [itemComments, setItemComments] = useState({});
  const [loadingComments, setLoadingComments] = useState({});
  const [stats, setStats] = useState({
    total: 0,
    byType: {},
    byParentType: {},
  });

  useEffect(() => {
    fetchFeedItems();
  }, [filters]);

  const fetchFeedItems = async () => {
    try {
      setLoading(true);
      const response = await apiService.getFeedItems({
        parentType:
          filters.parentType !== "all" ? filters.parentType : undefined,
        type: filters.type !== "all" ? filters.type : undefined,
        search: filters.search || undefined,
        limit: 100,
      });

      setFeedItems(response.data || []);
      calculateStats(response.data || []);
    } catch (error) {
      console.error("Error fetching feed items:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async (feedItemId) => {
    try {
      setLoadingComments((prev) => ({ ...prev, [feedItemId]: true }));
      const response = await apiService.getFeedItemComments(feedItemId);
      setItemComments((prev) => ({
        ...prev,
        [feedItemId]: response.data || [],
      }));
    } catch (error) {
      console.error("Error fetching comments:", error);
      setItemComments((prev) => ({
        ...prev,
        [feedItemId]: [],
      }));
    } finally {
      setLoadingComments((prev) => ({ ...prev, [feedItemId]: false }));
    }
  };

  const calculateStats = (items) => {
    const byType = {};
    const byParentType = {};

    items.forEach((item) => {
      byType[item.type] = (byType[item.type] || 0) + 1;
      byParentType[item.parentType] = (byParentType[item.parentType] || 0) + 1;
    });

    setStats({
      total: items.length,
      byType,
      byParentType,
    });
  };

  const toggleExpanded = (itemId) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
        if (!itemComments[itemId] && !loadingComments[itemId]) {
          fetchComments(itemId);
        }
      }
      return newSet;
    });
  };

  const getParentIcon = (parentType) => {
    const icons = {
      Case: AlertCircle,
      Account: Building2,
      Opportunity: Briefcase,
      Contact: Users,
      Lead: User,
      Project: FileText,
    };
    return icons[parentType] || FileText;
  };

  const getTypeIcon = (type) => {
    const icons = {
      TextPost: MessageSquare,
      EmailMessageEvent: Mail,
      ContentPost: FileText,
      LinkPost: LinkIcon,
      CallLogPost: Phone,
    };
    return icons[type] || MessageSquare;
  };

  const getTypeColor = (type) => {
    const colors = {
      TextPost: "text-blue-400 bg-blue-400/10",
      EmailMessageEvent: "text-purple-400 bg-purple-400/10",
      ContentPost: "text-green-400 bg-green-400/10",
      LinkPost: "text-orange-400 bg-orange-400/10",
      CallLogPost: "text-pink-400 bg-pink-400/10",
    };
    return colors[type] || "text-gray-400 bg-gray-400/10";
  };

  const getParentColor = (parentType) => {
    const colors = {
      Case: "text-red-400 bg-red-400/10",
      Account: "text-blue-400 bg-blue-400/10",
      Opportunity: "text-green-400 bg-green-400/10",
      Contact: "text-purple-400 bg-purple-400/10",
      Lead: "text-orange-400 bg-orange-400/10",
      Project: "text-cyan-400 bg-cyan-400/10",
    };
    return colors[parentType] || "text-gray-400 bg-gray-400/10";
  };

  const navigateToParent = (item) => {
    if (!item.parentId) return;

    const routes = {
      Case: `/cases/${item.parentId}`,
      Account: `/accounts/${item.parentId}`,
      Opportunity: `/opportunities/${item.parentId}`,
      Contact: `/contacts/${item.parentId}`,
      Lead: `/leads/${item.parentId}`,
      Project: `/projects/${item.parentId}`,
    };

    const route = routes[item.parentType];
    if (route) {
      navigate(route);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const StatCard = ({ title, value, icon: Icon, color = "text-blue-400" }) => (
    <div
      className={`${isDarkMode ? "bg-[#0C0E12] border-gray-700/50" : "bg-white border-gray-200"} rounded-xl p-6 border`}
    >
      <div className="flex items-center justify-between mb-2">
        <div
          className={`text-sm font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
        >
          {title}
        </div>
        {Icon && <Icon size={20} className={color} />}
      </div>
      <div
        className={`text-3xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
      >
        {value}
      </div>
    </div>
  );

  const CommentItem = ({ comment }) => {
    const creator = comment.creator || comment.salesforceCreator;

    return (
      <div
        className={`${isDarkMode ? "bg-[#0A0C10] border-gray-700/50" : "bg-gray-100 border-gray-200"} rounded-lg p-4 border`}
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            {creator?.profileImageUrl ? (
              <img
                src={creator.profileImageUrl}
                alt={creator.name}
                className="w-8 h-8 rounded-full"
              />
            ) : (
              <div
                className={`w-8 h-8 rounded-full ${isDarkMode ? "bg-gray-700" : "bg-gray-300"} flex items-center justify-center`}
              >
                <User
                  size={16}
                  className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span
                className={`font-medium text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                {creator?.name || "Unknown User"}
              </span>
              <span
                className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
              >
                {formatDate(comment.salesforceCreatedDate || comment.createdAt)}
              </span>
              {comment.commentType && (
                <span
                  className={`text-xs px-2 py-0.5 rounded ${isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-200 text-gray-600"}`}
                >
                  {comment.commentType}
                </span>
              )}
            </div>
            <div
              className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"} whitespace-pre-wrap`}
            >
              {comment.commentBody || "No content"}
            </div>
            {comment.isVerified && (
              <div className="mt-2">
                <span className="text-xs px-2 py-0.5 rounded bg-green-500/20 text-green-400">
                  ✓ Verified
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const FeedItemCard = ({ item }) => {
    const isExpanded = expandedItems.has(item.id);
    const TypeIcon = getTypeIcon(item.type);
    const ParentIcon = getParentIcon(item.parentType);
    const comments = itemComments[item.id] || [];
    const isLoadingComments = loadingComments[item.id];

    return (
      <div
        className={`${isDarkMode ? "bg-[#0C0E12] border-gray-700 hover:border-gray-600" : "bg-white border-gray-200 hover:border-gray-300"} border rounded-lg overflow-hidden transition-colors`}
      >
        {/* Header */}
        <div
          className={`p-4 border-b ${isDarkMode ? "border-gray-700" : "border-gray-200"}`}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <span
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${getTypeColor(item.type)}`}
                >
                  <TypeIcon size={12} />
                  {item.type}
                </span>
                <span
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${getParentColor(item.parentType)}`}
                >
                  <ParentIcon size={12} />
                  {item.parentType}
                </span>
                {item.isDeleted && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-red-400 bg-red-400/10">
                    Deleted
                  </span>
                )}
              </div>

              {item.title && (
                <h3
                  className={`font-medium mb-1 line-clamp-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  {item.title}
                </h3>
              )}

              {item.body && (
                <p
                  className={`text-sm line-clamp-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  {item.body}
                </p>
              )}

              <div
                className={`flex items-center gap-4 mt-2 text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
              >
                {(item.creator || item.salesforceCreator) && (
                  <div className="flex items-center gap-1">
                    <User size={12} />
                    {item.creator?.name || item.salesforceCreator?.name}
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <Calendar size={12} />
                  {formatDate(item.salesforceCreatedDate)}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {item.parentId && (
                <button
                  onClick={() => navigateToParent(item)}
                  className={`p-2 ${isDarkMode ? "hover:bg-gray-800 text-gray-400 hover:text-white" : "hover:bg-gray-100 text-gray-500 hover:text-gray-900"} rounded transition-colors`}
                  title="View Parent"
                >
                  <ExternalLink size={16} />
                </button>
              )}
              <button
                onClick={() => toggleExpanded(item.id)}
                className={`p-2 ${isDarkMode ? "hover:bg-gray-800 text-gray-400 hover:text-white" : "hover:bg-gray-100 text-gray-500 hover:text-gray-900"} rounded transition-colors`}
                title={isExpanded ? "Collapse" : "Expand"}
              >
                {isExpanded ? (
                  <ChevronUp size={16} />
                ) : (
                  <ChevronDown size={16} />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Stats Row */}
        <div
          className={`px-4 py-3 ${isDarkMode ? "bg-gray-800/30 border-gray-700" : "bg-gray-100/50 border-gray-200"} border-b flex items-center gap-6 text-sm`}
        >
          <div
            className={`flex items-center gap-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            <MessageSquare size={14} />
            <span
              className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              {item.commentCount || 0}
            </span>
            <span>Comments</span>
          </div>
          <div
            className={`flex items-center gap-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            <ThumbsUp size={14} />
            <span
              className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
            >
              {item.likeCount || 0}
            </span>
            <span>Likes</span>
          </div>
          {item.attachments && item.attachments.length > 0 && (
            <div
              className={`flex items-center gap-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              <Paperclip size={14} />
              <span
                className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                {item.attachments.length}
              </span>
              <span>Attachments</span>
            </div>
          )}
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div
            className={`p-4 space-y-4 ${isDarkMode ? "bg-gray-800/20" : "bg-gray-100/30"}`}
          >
            {item.body && (
              <div>
                <h4
                  className={`text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"} mb-2`}
                >
                  Content
                </h4>
                <div
                  className={`${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-white border-gray-200"} p-4 rounded border`}
                >
                  <p
                    className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"} whitespace-pre-wrap`}
                  >
                    {item.body}
                  </p>
                </div>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-3">
                <h4
                  className={`text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"} flex items-center gap-2`}
                >
                  <MessageSquare size={16} />
                  Comments ({comments.length})
                </h4>
                {!isLoadingComments && (
                  <button
                    onClick={() => fetchComments(item.id)}
                    className="text-xs text-blue-400 hover:text-blue-300 flex items-center gap-1"
                  >
                    <RefreshCw size={12} />
                    Refresh
                  </button>
                )}
              </div>

              {isLoadingComments ? (
                <div
                  className={`${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-white border-gray-200"} p-6 rounded border text-center`}
                >
                  <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                  <p
                    className={`mt-2 text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Loading comments...
                  </p>
                </div>
              ) : comments.length > 0 ? (
                <div className="space-y-3">
                  {comments.map((comment) => (
                    <CommentItem key={comment.id} comment={comment} />
                  ))}
                </div>
              ) : (
                <div
                  className={`${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-white border-gray-200"} p-6 rounded border text-center`}
                >
                  <MessageSquare
                    size={32}
                    className={`mx-auto mb-2 ${isDarkMode ? "text-gray-600" : "text-gray-400"}`}
                  />
                  <p
                    className={`text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    No comments yet
                  </p>
                </div>
              )}
            </div>

            {item.attachments && item.attachments.length > 0 && (
              <div>
                <h4
                  className={`text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"} mb-2 flex items-center gap-2`}
                >
                  <Paperclip size={16} />
                  Attachments ({item.attachments.length})
                </h4>
                <div className="space-y-2">
                  {item.attachments.map((attachment) => (
                    <div
                      key={attachment.id}
                      className={`${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-white border-gray-200"} p-3 rounded border flex items-center gap-3`}
                    >
                      <FileText
                        size={16}
                        className={
                          isDarkMode ? "text-gray-400" : "text-gray-500"
                        }
                      />
                      <div className="flex-1 min-w-0">
                        <div
                          className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                        >
                          {attachment.title || "Untitled"}
                        </div>
                        {attachment.type && (
                          <div
                            className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                          >
                            {attachment.type}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div
                className={`${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-white border-gray-200"} p-3 rounded border`}
              >
                <div
                  className={`flex items-center gap-2 text-xs mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  <MessageSquare size={12} />
                  Comments
                </div>
                <div
                  className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  {item.commentCount || 0}
                </div>
              </div>
              <div
                className={`${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-white border-gray-200"} p-3 rounded border`}
              >
                <div
                  className={`flex items-center gap-2 text-xs mb-1 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  <ThumbsUp size={12} />
                  Likes
                </div>
                <div
                  className={`text-lg font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  {item.likeCount || 0}
                </div>
              </div>
            </div>

            <div>
              <h4
                className={`text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"} mb-2`}
              >
                Metadata
              </h4>
              <div
                className={`${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-white border-gray-200"} p-3 rounded border space-y-2 text-xs`}
              >
                <div className="flex justify-between">
                  <span
                    className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                  >
                    Visibility:
                  </span>
                  <span className={isDarkMode ? "text-white" : "text-gray-900"}>
                    {item.visibility || "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span
                    className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                  >
                    Status:
                  </span>
                  <span className={isDarkMode ? "text-white" : "text-gray-900"}>
                    {item.status || "N/A"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span
                    className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                  >
                    Published:
                  </span>
                  <span className={isDarkMode ? "text-white" : "text-gray-900"}>
                    {item.isPublished ? "Yes" : "No"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span
                    className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                  >
                    Revision:
                  </span>
                  <span className={isDarkMode ? "text-white" : "text-gray-900"}>
                    {item.revision || 0}
                  </span>
                </div>
                {item.relatedRecordId && (
                  <div className="flex justify-between">
                    <span
                      className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                    >
                      Related Record:
                    </span>
                    <span
                      className={`font-mono text-xs ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {item.relatedRecordId}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div>
              <h4
                className={`text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"} mb-2`}
              >
                Integration IDs
              </h4>
              <div
                className={`${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-white border-gray-200"} p-3 rounded border space-y-2 text-xs`}
              >
                <div>
                  <span
                    className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                  >
                    Salesforce ID:
                  </span>
                  <div
                    className={`font-mono mt-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {item.salesforceId}
                  </div>
                </div>
                {item.salesforceCreatedBySfId && (
                  <div>
                    <span
                      className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                    >
                      Created By (SF):
                    </span>
                    <div
                      className={`font-mono mt-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {item.salesforceCreatedBySfId}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={`min-h-screen font-inter transition-colors duration-300 ${isDarkMode ? "bg-[#0C0E12] text-white" : "bg-[#F4F5F7] text-gray-900"}`}
    >
      <div className="p-4 lg:p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-1">
              Feed Items
            </h1>
            <p
              className={`text-sm sm:text-base ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              Activity feed across all records
            </p>
          </div>

          <button
            onClick={fetchFeedItems}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            title="Total Feed Items"
            value={stats.total}
            icon={TrendingUp}
            color="text-blue-400"
          />
          <StatCard
            title="Case Feed Items"
            value={stats.byParentType.Case || 0}
            icon={AlertCircle}
            color="text-red-400"
          />
          <StatCard
            title="Account Feed Items"
            value={stats.byParentType.Account || 0}
            icon={Building2}
            color="text-green-400"
          />
          <StatCard
            title="Opportunity Feed Items"
            value={stats.byParentType.Opportunity || 0}
            icon={Briefcase}
            color="text-purple-400"
          />
        </div>

        {/* Filters */}
        <div
          className={`${isDarkMode ? "bg-[#0C0E12] border-gray-700" : "bg-white border-gray-200"} border rounded-lg p-4 mb-6`}
        >
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <label
                className={`block text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"} mb-2`}
              >
                Search
              </label>
              <div className="relative">
                <Search
                  className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  size={16}
                />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) =>
                    setFilters({ ...filters, search: e.target.value })
                  }
                  placeholder="Search feed items..."
                  className={`w-full pl-10 pr-4 py-2 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white placeholder-gray-400" : "bg-gray-100 border-gray-200 text-gray-900 placeholder-gray-500"} border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
              </div>
            </div>

            <div>
              <label
                className={`block text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"} mb-2`}
              >
                Parent Type
              </label>
              <select
                value={filters.parentType}
                onChange={(e) =>
                  setFilters({ ...filters, parentType: e.target.value })
                }
                className={`w-full px-3 py-2 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-gray-100 border-gray-200 text-gray-900"} border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
              >
                <option value="all">All Types</option>
                <option value="Case">Case</option>
                <option value="Account">Account</option>
                <option value="Opportunity">Opportunity</option>
                <option value="Contact">Contact</option>
                <option value="Lead">Lead</option>
                <option value="Project">Project</option>
              </select>
            </div>

            <div>
              <label
                className={`block text-sm font-medium ${isDarkMode ? "text-gray-300" : "text-gray-700"} mb-2`}
              >
                Feed Type
              </label>
              <select
                value={filters.type}
                onChange={(e) =>
                  setFilters({ ...filters, type: e.target.value })
                }
                className={`w-full px-3 py-2 ${isDarkMode ? "bg-gray-800 border-gray-700 text-white" : "bg-gray-100 border-gray-200 text-gray-900"} border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
              >
                <option value="all">All Types</option>
                <option value="TextPost">Text Post</option>
                <option value="EmailMessageEvent">Email</option>
                <option value="ContentPost">Content</option>
                <option value="LinkPost">Link</option>
                <option value="CallLogPost">Call Log</option>
              </select>
            </div>
          </div>
        </div>

        {/* Feed Items List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            <p
              className={`mt-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              Loading feed items...
            </p>
          </div>
        ) : feedItems.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare
              size={48}
              className={`mx-auto mb-4 ${isDarkMode ? "text-gray-600" : "text-gray-400"}`}
            />
            <p className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
              No feed items found
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {feedItems.map((item) => (
              <FeedItemCard key={item.id} item={item} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedItems;
